import React, { useState } from 'react';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import { FaX, FaChevronDown, FaChevronUp, FaUsers } from 'react-icons/fa6';
import './ReceiptDetails.scss';

interface Selection {
  selection_id: string;
  item_name: string;
  quantity: number;
  total_price: number;
  base_price: number;
  comment?: string;
  clientIndex?: number; // -1 means shared by group
  modifiers: Array<{
    option_id: number;
    modifier_id: number;
    option_name: string;
  }>;
  selection_groups: Array<{
    selection_group_id: number;
    selected_items: Array<{
      item_id: number;
      quantity: number;
      item_name: string;
    }>;
  }>;
}

interface ReceiptDetailsProps {
  selections: Selection[];
  subtotal: number;
  totalReceipts?: number; // Number of receipts for pro-rating shared items
}

const ReceiptDetails: React.FC<ReceiptDetailsProps> = ({ selections, subtotal, totalReceipts = 1 }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate display price - pro-rate for shared items (clientIndex === -1)
  const getDisplayPrice = (selection: Selection): number => {
    if (selection.clientIndex === -1 && totalReceipts > 1) {
      return selection.total_price / totalReceipts;
    }
    return selection.total_price;
  };

  // Check if item is shared
  const isSharedItem = (selection: Selection): boolean => {
    return selection.clientIndex === -1;
  };

  return (
    <div className="receipt-details">
      <div className="details-header" onClick={toggleExpanded}>
        <div className="header-content">
          <h4>Detalles del Pedido</h4>
          <span className="items-count">{selections.length} productos</span>
        </div>
        <div className="toggle-icon">
          {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
        </div>
      </div>
      
      {isExpanded && (
        <div className="details-content">
          <div className="selections-list">
            {selections.map((selection) => (
              <div key={selection.selection_id} className={`selection-item ${isSharedItem(selection) ? 'shared-item' : ''}`}>
                <div className="item-main">
                  <div className="quantity-and-name">
                    <span className="quantity">
                      {selection.quantity} <FaX size={8} />
                    </span>
                    <span className="item-name">
                      {selection.item_name}
                      {isSharedItem(selection) && (
                        <span className="shared-badge" title="Compartido por el grupo">
                          <FaUsers size={10} />
                          <span className="badge-text">Compartido</span>
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="item-price">
                    {isSharedItem(selection) && totalReceipts > 1 ? (
                      <span className="prorated-price">
                        <span className="original-price">{formatCurrency(selection.total_price)}</span>
                        <span className="divided-price">{formatCurrency(getDisplayPrice(selection))}</span>
                      </span>
                    ) : (
                      formatCurrency(selection.total_price)
                    )}
                  </div>
                </div>

                {/* Selection Groups (toppings, sides, etc.) */}
                {selection.selection_groups.length > 0 && (
                  <div className="selection-groups">
                    {selection.selection_groups.map((group) => (
                      <div key={group.selection_group_id} className="group-items">
                        {group.selected_items.map((item) => (
                          <span key={item.item_id} className="group-item">
                            + {item.item_name}
                            {item.quantity > 1 && ` (${item.quantity})`}
                          </span>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Modifiers */}
                {selection.modifiers.length > 0 && (
                  <div className="modifiers">
                    {selection.modifiers.map((modifier) => (
                      <span key={modifier.option_id} className="modifier-item">
                        • {modifier.option_name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Comment */}
                {selection.comment && selection.comment.trim() !== '' && (
                  <div className="item-comment">
                    <span className="comment-label">Nota:</span>
                    <span className="comment-text">{selection.comment}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="details-summary">
            <div className="summary-row">
              <span className="summary-label">Subtotal</span>
              <span className="summary-value">{formatCurrency(subtotal)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReceiptDetails;
